import { useEffect, useState } from "react";
import { api, formatCents, formatDate } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select } from "@/components/ui/Primitives";
import { Receipt, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";

const CATEGORIES = [
  "mortgage", "utilities", "repairs", "insurance",
  "property_tax", "cleaning", "management", "other"
];
const CATEGORY_TONE = {
  mortgage: "violet", utilities: "blue", repairs: "amber",
  insurance: "green", property_tax: "red", cleaning: "slate",
  management: "blue", other: "slate"
};

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    property_id: "", category: "other", description: "", amount: "",
    date: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    setLoading(true);
    try {
      const [e, s, p] = await Promise.all([
        api.get("/expenses").catch(() => ({ data: [] })),
        api.get("/expenses/summary").catch(() => ({ data: {} })),
        api.get("/properties").catch(() => ({ data: [] })),
      ]);
      setItems(e.data || []);
      setSummary(s.data);
      setProperties(p.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/expenses", {
        property_id: form.property_id || null,
        category: form.category,
        description: form.description.trim(),
        amount: Math.round(Number(form.amount) * 100),
        date: form.date,
      });
      toast.success("Expense logged");
      setOpen(false);
      setForm({ property_id: "", category: "other", description: "", amount: "", date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add expense");
    } finally { setSaving(false); }
  };

  const remove = async (e) => {
    const ok = await confirmDialog({
      title: "Delete this expense?",
      description: `${e.description || "Expense"} · ${formatCents(e.amount)}\n\nThis cannot be undone.`,
      confirmLabel: "Yes, delete",
      cancelLabel: "Keep",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/expenses/${e.expense_id}`);
      toast.success("Expense deleted");
      load();
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  const exportCsv = async () => {
    try {
      await downloadFile("/reports/landlord/export.csv", `crc-report-${Date.now()}.csv`);
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Expenses"
        subtitle="Track every cost across your portfolio — and breeze through tax season."
        actions={
          <>
            <Button variant="secondary" onClick={exportCsv} data-testid="export-csv-button">Export CSV</Button>
            <Button onClick={() => setOpen(true)} data-testid="add-expense-button"><Plus className="w-4 h-4"/> Log expense</Button>
          </>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard label="Total this year" value={formatCents(summary.total_ytd || summary.total || 0)} tone="green"/>
          <SummaryCard label="This month" value={formatCents(summary.total_mtd || summary.month_total || 0)} tone="blue"/>
          <SummaryCard label="Records" value={items.length} tone="slate"/>
          <SummaryCard label="Top category" value={summary.top_category || "—"} tone="amber" small/>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No expenses yet"
          description="Log expenses as they happen to make tax-season reporting effortless."
          action={null}
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 font-semibold">Date</th>
                  <th className="px-5 py-3 font-semibold">Category</th>
                  <th className="px-5 py-3 font-semibold">Description</th>
                  <th className="px-5 py-3 font-semibold text-right">Amount</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.expense_id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`expense-row-${e.expense_id}`}>
                    <td className="px-5 py-4 text-slate-700">{formatDate(e.date)}</td>
                    <td className="px-5 py-4"><Pill tone={CATEGORY_TONE[e.category] || "slate"}>{e.category.replace("_"," ")}</Pill></td>
                    <td className="px-5 py-4 font-semibold text-slate-900">{e.description}</td>
                    <td className="px-5 py-4 text-right font-semibold text-slate-900">{formatCents(e.amount)}</td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => remove(e)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`delete-expense-${e.expense_id}`}>
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Log an expense" testid="add-expense-modal">
        <form onSubmit={submit} className="space-y-4">
          <Select label="Category" value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} testid="expense-category-select">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("_"," ")}</option>)}
          </Select>
          <Input label="Description" required value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} testid="expense-description-input"/>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Amount (CAD)" type="number" step="0.01" min="0" required value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} testid="expense-amount-input"/>
            <Input label="Date" type="date" required value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} testid="expense-date-input"/>
          </div>
          {properties.length > 0 && (
            <Select label="Property (optional)" value={form.property_id} onChange={(e)=>setForm({...form, property_id:e.target.value})} testid="expense-property-select">
              <option value="">— None / Portfolio-wide —</option>
              {properties.map((p) => <option key={p.property_id} value={p.property_id}>{p.name}</option>)}
            </Select>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="expense-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="expense-create-submit">{saving ? "Saving…" : "Log expense"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function SummaryCard({ label, value, tone = "slate", small = false }) {
  const toneClass = {
    green: "bg-green-50 text-green-800",
    blue: "bg-blue-50 text-blue-800",
    amber: "bg-amber-50 text-amber-800",
    slate: "bg-slate-50 text-slate-800",
  }[tone];
  return (
    <Card className="p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      <div className={`mt-2 ${small ? "text-xl" : "font-display text-2xl"} font-bold text-slate-950 ${small ? "capitalize" : ""}`}>{value}</div>
      <div className={`mt-3 inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${toneClass}`}>summary</div>
    </Card>
  );
}
